import React, { useState, useEffect } from 'react';
import InputField from '../common/InputField';
import SelectField from '../common/SelectField';
import Button from '../common/Button';
import { Package, Tag, DollarSign, Calendar, Barcode } from 'lucide-react';

const ProductForm = ({ product, onSave, categories = [] }) => {
  const [formData, setFormData] = useState(product || {});

  useEffect(() => {
    setFormData(product || {});
  }, [product]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const dataToSave = {
      ...formData,
      threshold: Number(formData.threshold || 0),
      quantity: 0, // Inventory managed separately
      costPrice: Number(formData.costPrice || 0),
      sellingPrice: Number(formData.sellingPrice || 0),
      categoryId: formData.categoryId || categories.find(cat => cat.name === formData.categoryName)?.id || '',
      expiry: formData.expiry || null,
    };
    onSave(dataToSave);
  };

  const categoryOptions = categories.map(cat => ({ value: cat.id, label: cat.name }));

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <InputField
            label="Product Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="e.g. Paracetamol 500mg"
            required
            icon={<Package className="w-4 h-4" />}
          />
        </div>

        <div className="sm:col-span-2">
          <InputField
            label="Description (Optional)"
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Brief item description or usage instructions"
          />
        </div>

        <SelectField
          label="Category"
          name="categoryId"
          value={formData.categoryId}
          onChange={handleChange}
          options={[{ value: '', label: 'Select Category' }, ...categoryOptions]}
          icon={<Tag className="w-4 h-4" />}
        />

        <InputField
          label="Brand / Manufacturer"
          name="brand"
          value={formData.brand}
          onChange={handleChange}
          placeholder="e.g. Pfizer, GSK"
        />

        <InputField
          label="Barcode / SKU"
          name="barcode"
          value={formData.barcode}
          onChange={handleChange}
          placeholder="e.g. 890123456789"
          icon={<Barcode className="w-4 h-4" />}
        />

        <InputField
          label="Low Stock Threshold"
          type="number"
          name="threshold"
          value={formData.threshold}
          onChange={handleChange}
          placeholder="Alert level (e.g. 10)"
        />

        <InputField
          label="Cost Price"
          type="number"
          name="costPrice"
          value={formData.costPrice}
          onChange={handleChange}
          step="0.01"
          placeholder="0.00"
          icon={<DollarSign className="w-4 h-4" />}
          required
        />

        <InputField
          label="Selling Price"
          type="number"
          name="sellingPrice"
          value={formData.sellingPrice}
          onChange={handleChange}
          step="0.01"
          placeholder="0.00"
          icon={<DollarSign className="w-4 h-4" />}
          required
        />

        <div className="sm:col-span-2">
          <InputField
            label="Expiry Date"
            type="date"
            name="expiry"
            value={formData.expiry ? formData.expiry.split('T')[0] : ''}
            onChange={handleChange}
            icon={<Calendar className="w-4 h-4" />}
          />
        </div>
      </div>

      <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/80">
        <input
          type="checkbox"
          id="trackInventory"
          name="trackInventory"
          checked={formData.trackInventory ?? true}
          onChange={handleChange}
          className="w-4 h-4 text-emerald-600 bg-white dark:bg-slate-700 border-slate-300 dark:border-slate-600 rounded focus:ring-emerald-500 focus:ring-2 cursor-pointer"
        />
        <label htmlFor="trackInventory" className="text-xs font-semibold text-slate-700 dark:text-slate-200 cursor-pointer select-none">
          Enable Stock Tracking for this item
        </label>
      </div>

      <div className="pt-2">
        <Button type="submit" className="w-full py-3 text-sm font-semibold">
          {product?.id ? 'Update Product' : 'Create Product'}
        </Button>
      </div>
    </form>
  );
};

export default ProductForm;
